import { useState, useCallback, useRef, useEffect } from 'react';
import { InputSanitizer } from '../lib/security/sanitizer';

interface UseSanitizedInputOptions {
  type?: 'text' | 'email' | 'url' | 'number' | 'search' | 'username' | 'tenantCode';
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  pattern?: RegExp;
  sanitizer?: (value: string) => string | null;
  validator?: (value: string) => boolean;
  debounceMs?: number;
}

interface SanitizedInputState {
  value: string;
  sanitizedValue: string | null;
  isValid: boolean;
  error: string | null;
  isDirty: boolean;
  isTouched: boolean;
}

export function useSanitizedInput(
  initialValue: string = '',
  options: UseSanitizedInputOptions = {}
) {
  const [state, setState] = useState<SanitizedInputState>({
    value: initialValue,
    sanitizedValue: initialValue,
    isValid: true,
    error: null,
    isDirty: false,
    isTouched: false
  });

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Default sanitizers based on type
  const getSanitizer = useCallback(() => {
    if (options.sanitizer) return options.sanitizer;

    switch (options.type) {
      case 'email':
        return InputSanitizer.sanitizeEmail;
      case 'url':
        return InputSanitizer.sanitizeUrl;
      case 'username':
        return InputSanitizer.sanitizeUsername;
      case 'tenantCode':
        return InputSanitizer.sanitizeTenantCode;
      case 'search':
        return (v: string) => InputSanitizer.sanitizeSearchQuery(v, options.maxLength);
      case 'number':
        return (v: string) => {
          const num = InputSanitizer.sanitizeNumber(v);
          return num !== null ? num.toString() : null;
        };
      default:
        return (v: string) => InputSanitizer.stripHtml(v);
    }
  }, [options.type, options.sanitizer, options.maxLength]);

  // Validate input
  const validate = useCallback((value: string, sanitizedValue: string | null): string | null => {
    // Check if dangerous content
    if (InputSanitizer.containsDangerousContent(value)) {
      return 'İçerik güvenlik nedeniyle reddedildi';
    }

    // Required check
    if (options.required && !sanitizedValue) {
      return 'Bu alan zorunludur';
    }

    // Length checks
    if (sanitizedValue) {
      if (options.minLength && sanitizedValue.length < options.minLength) {
        return `En az ${options.minLength} karakter olmalıdır`;
      }
      if (options.maxLength && sanitizedValue.length > options.maxLength) {
        return `En fazla ${options.maxLength} karakter olabilir`;
      }
    }

    // Pattern check
    if (options.pattern && sanitizedValue && !options.pattern.test(sanitizedValue)) {
      return 'Geçersiz format';
    }

    // Custom validator
    if (options.validator && sanitizedValue && !options.validator(sanitizedValue)) {
      return 'Geçersiz değer';
    }

    // Type-specific validation messages
    if (!sanitizedValue && value) {
      switch (options.type) {
        case 'email':
          return 'Geçerli bir e-posta adresi girin';
        case 'url':
          return 'Geçerli bir URL girin';
        case 'username':
          return 'Kullanıcı adı 3-20 karakter arasında olmalı ve sadece harf, rakam ve _ içerebilir';
        case 'tenantCode':
          return 'Kurum kodu 4-10 karakter arasında büyük harf ve rakam içermelidir';
        case 'number':
          return 'Geçerli bir sayı girin';
      }
    }

    return null;
  }, [options]);

  // Handle value change
  const handleChange = useCallback((newValue: string) => {
    // Clear previous debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Update raw value immediately
    setState(prev => ({
      ...prev,
      value: newValue,
      isDirty: true
    }));

    // Debounce sanitization and validation
    const processValue = () => {
      const sanitizer = getSanitizer();
      const sanitizedValue = sanitizer(newValue);
      const error = validate(newValue, sanitizedValue);

      setState(prev => ({
        ...prev,
        sanitizedValue,
        isValid: !error,
        error
      }));
    };

    if (options.debounceMs) {
      debounceTimer.current = setTimeout(processValue, options.debounceMs);
    } else {
      processValue();
    }
  }, [getSanitizer, validate, options.debounceMs]);

  // Handle blur
  const handleBlur = useCallback(() => {
    setState(prev => ({
      ...prev,
      isTouched: true
    }));
  }, []);

  // Reset state
  const reset = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    setState({
      value: initialValue,
      sanitizedValue: initialValue,
      isValid: true,
      error: null,
      isDirty: false,
      isTouched: false
    });
  }, [initialValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return {
    ...state,
    handleChange,
    handleBlur,
    reset,
    showError: state.isDirty && state.isTouched && !state.isValid
  };
}

// Hook for sanitizing form data
export function useSanitizedForm<T extends Record<string, any>>(
  initialValues: T,
  schema: Record<keyof T, UseSanitizedInputOptions>
) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  
  const inputs = useRef<Record<keyof T, ReturnType<typeof useSanitizedInput>>>({} as any);

  // Initialize inputs
  useEffect(() => {
    const newInputs: any = {};
    
    for (const [key, options] of Object.entries(schema) as [keyof T, UseSanitizedInputOptions][]) {
      const initialValue = String(initialValues[key] || '');
      // eslint-disable-next-line react-hooks/rules-of-hooks
      newInputs[key] = useSanitizedInput(initialValue, options);
    }
    
    inputs.current = newInputs;
  }, []);

  // Handle field change
  const handleFieldChange = useCallback((field: keyof T, value: string) => {
    if (inputs.current[field]) {
      inputs.current[field].handleChange(value);
      
      setValues(prev => ({
        ...prev,
        [field]: value
      }));
      
      // Update errors
      setErrors(prev => ({
        ...prev,
        [field]: inputs.current[field].error
      }));
    }
  }, []);

  // Handle field blur
  const handleFieldBlur = useCallback((field: keyof T) => {
    if (inputs.current[field]) {
      inputs.current[field].handleBlur();
      
      setTouched(prev => ({
        ...prev,
        [field]: true
      }));
    }
  }, []);

  // Get sanitized values
  const getSanitizedValues = useCallback((): Partial<T> => {
    const sanitized: Partial<T> = {};
    
    for (const key of Object.keys(schema) as (keyof T)[]) {
      const input = inputs.current[key];
      if (input && input.sanitizedValue !== null) {
        sanitized[key] = input.sanitizedValue as any;
      }
    }
    
    return sanitized;
  }, [schema]);

  // Validate all fields
  const validateAll = useCallback((): boolean => {
    let isValid = true;
    const newErrors: Partial<Record<keyof T, string>> = {};
    
    for (const key of Object.keys(schema) as (keyof T)[]) {
      const input = inputs.current[key];
      if (input && input.error) {
        isValid = false;
        newErrors[key] = input.error;
      }
    }
    
    setErrors(newErrors);
    return isValid;
  }, [schema]);

  // Reset form
  const reset = useCallback(() => {
    for (const input of Object.values(inputs.current)) {
      if (input) {
        (input as any).reset();
      }
    }
    
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  return {
    values,
    errors,
    touched,
    handleFieldChange,
    handleFieldBlur,
    getSanitizedValues,
    validateAll,
    reset,
    isValid: Object.keys(errors).length === 0
  };
}